import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Users, Storefront, UsersThree, Globe } from "@phosphor-icons/react";
import { getStats } from "@/lib/api";

const ITEMS = [
  { key: "drivers_signed_up", label: "Drivers onboarding", icon: Users, color: "#D4AF37" },
  { key: "merchants_signed_up", label: "Merchant partners", icon: Storefront, color: "#14B8A6" },
  { key: "waitlist_size", label: "On the waitlist", icon: UsersThree, color: "#FF7A45" },
  { key: "service_categories", label: "Service categories", icon: Globe, color: "#FB7185", suffix: "" },
];

function useCount(target, duration = 1200) {
  const [n, setN] = useState(0);
  const animatedFrom = useRef(0);
  const lastTarget = useRef(null);

  useEffect(() => {
    // Only animate when the target actually changes
    if (lastTarget.current === target) return;
    const start = animatedFrom.current;
    const delta = target - start;
    lastTarget.current = target;

    let raf;
    let startTs = null;
    const animate = (ts) => {
      if (startTs === null) startTs = ts;
      const elapsed = ts - startTs;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = Math.round(start + delta * eased);
      setN(next);
      animatedFrom.current = next;
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}

const StatCard = ({ value, label, Icon, color, delay }) => {
  const animated = useCount(value, 1300);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay }}
      className="bg-ih-surface border border-ih-line rounded-2xl p-6 flex items-center justify-between"
      data-testid={`live-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div>
        <p className="text-xs uppercase tracking-[0.16em] text-white/40">{label}</p>
        <p className="font-display font-extrabold text-4xl text-white mt-1 tabular-nums">{animated.toLocaleString()}</p>
      </div>
      <span className="w-12 h-12 rounded-xl bg-black/40 border border-ih-line2 grid place-items-center" style={{ color }}>
        <Icon weight="duotone" size={22} />
      </span>
    </motion.div>
  );
};

export default function LiveStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const s = await getStats();
        if (mounted) setStats(s);
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, 20000); // refresh every 20s
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  if (!stats) return null;

  return (
    <section className="relative py-16 border-t border-ih-line" data-testid="live-stats">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-2 h-2 rounded-full bg-ih-neon animate-pulse-glow" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/60">Live network · updates every 20s</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {ITEMS.map((it, i) => (
            <StatCard
              key={it.key}
              value={stats[it.key] ?? 0}
              label={it.label}
              Icon={it.icon}
              color={it.color}
              delay={i * 0.06}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
